import { renderToStaticMarkup } from 'react-dom/server';
import Highcharts from 'highcharts';
import {
  Icon,
  Inline,
  InlineItem,
  Stack,
  Strong,
  Text,
  ThemeProvider,
  Content,
} from '@kinesis/bungle';
import { Unit } from '@/types';
import { render } from '@/data/units';
import { TabularNumbers } from './visualisation-tooltip.styles';

// NOTE: Allows SVG, but places the burden of disabling user content on us.
(Highcharts.AST as any).filterUserAttributes = (a: any) => a;

const VisualisationTooltip = (props: VisualisationTooltipProps) => {
  const { title, entries = [] } = props;
  return (
    <TabularNumbers>
      <Content
        background='white'
        border
        shadow
        minWidth={240}
        padding='medium'
        paddingMode='equal'
        borderRadiusSize='large'
      >
        <Stack space='small'>
          {title && <Strong>{title}</Strong>}
          {entries.map(({ color, label, value, unit, icon }, index) => (
            <Inline key={index} space='small' alignY='center'>
              <InlineItem>
                {!icon && (
                  <Icon
                    display='block'
                    type='circle'
                    color={color}
                    magnitude='small'
                  />
                )}
                {icon === 'circle' && (
                  <Icon
                    display='block'
                    type='circle'
                    color={color}
                    magnitude='small'
                  />
                )}
                {icon === 'line' && (
                  <Icon
                    display='block'
                    type='custom'
                    color={color}
                    magnitude='small'
                  >
                    <svg
                      width='16'
                      height='2'
                      viewBox='0 0 16 2'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                    >
                      <rect width='16' height='2' />
                    </svg>
                  </Icon>
                )}
                {icon === 'dotted-line' && (
                  <Icon
                    display='block'
                    type='custom'
                    color={color}
                    magnitude='small'
                  >
                    <svg
                      width='16'
                      height='2'
                      viewBox='0 0 16 2'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                    >
                      <rect width='16' height='2' />
                      <rect x='4' width='2' height='2' fill='white' />
                      <rect x='10' width='2' height='2' fill='white' />
                    </svg>
                  </Icon>
                )}
              </InlineItem>
              <InlineItem sizing='fill-container'>
                <Text>{label}</Text>
              </InlineItem>
              <InlineItem>
                <Strong>{render(value, unit)}</Strong>
              </InlineItem>
            </Inline>
          ))}
        </Stack>
      </Content>
    </TabularNumbers>
  );
};

const renderVisualisationTooltip = (props: VisualisationTooltipProps) => {
  return renderToStaticMarkup(
    <ThemeProvider>
      <VisualisationTooltip title={props.title} entries={props.entries} />
    </ThemeProvider>,
  );
};

export type TooltipEntry = {
  color: string;
  label: string;
  value: number;
  unit: Unit;
  icon?: 'circle' | 'line' | 'dotted-line';
};

export type VisualisationTooltipProps = {
  title?: string;
  entries?: TooltipEntry[];
};

export { VisualisationTooltip, renderVisualisationTooltip };
